import { createContext, useCallback, useContext, useEffect, useSyncExternalStore } from "react";
import type { ProjectThreadList } from "../list/projectThreads";
import type { LiveConnectionState } from "./liveConnection";
import type { LiveThreadSnapshot, ThreadLiveClient } from "./threadLiveClient";

export const ThreadLiveContext = createContext<ThreadLiveClient | null>(null);

export function useThreadLiveClient(): ThreadLiveClient {
  const client = useContext(ThreadLiveContext);
  if (client === null) {
    throw new Error("useThreadLiveClient must be used inside ThreadLiveProvider.");
  }
  return client;
}

/**
 * Follow a Thread while the component is mounted. A page starts with recent entries, the
 * sidebar and the run graph with the last one; a later watcher that wants earlier entries has
 * them read in, and older ones are read on request (`loadEarlier`). `null` waits, for example
 * for the overview.
 */
export function useLiveThread(threadId: string, startAfter: number | null): LiveThreadSnapshot | undefined {
  const client = useThreadLiveClient();
  useEffect(() => (startAfter === null ? undefined : client.watch(threadId, startAfter)), [client, threadId, startAfter]);
  const read = useCallback(() => client.thread(threadId), [client, threadId]);
  return useSyncExternalStore(useCallback((listener) => client.subscribe(listener), [client]), read);
}

/** Follow a project's Thread list while the component is mounted (Q-62). */
export function useLiveProject(projectId: string, limit: number): ProjectThreadList | undefined {
  const client = useThreadLiveClient();
  useEffect(() => client.watchProject(projectId, limit), [client, projectId, limit]);
  const read = useCallback(() => client.project(projectId), [client, projectId]);
  return useSyncExternalStore(useCallback((listener) => client.subscribe(listener), [client]), read);
}

export function useLiveConnectionState(): LiveConnectionState {
  const client = useThreadLiveClient();
  return useSyncExternalStore(
    useCallback((listener) => client.subscribe(listener), [client]),
    () => client.connectionState(),
  );
}
